import type { RemoteThreadListAdapter } from '@assistant-ui/react';
import type { AiConversationThread } from '@restaurant/api-client';
import { createAssistantStream } from 'assistant-stream';
import {
  createThread,
  deleteThread,
  getThread,
  listThreads,
  updateThread,
} from '../api/assistant-api';

const toMetadata = (thread: AiConversationThread) => ({
  status: thread.status === 'archived' ? ('archived' as const) : ('regular' as const),
  remoteId: thread.id,
  title: thread.title,
  lastMessageAt: new Date(thread.lastMessageAt),
});

export function createThreadListAdapter(restaurantId: string): RemoteThreadListAdapter {
  return {
    async list() {
      const [active, archived] = await Promise.all([
        listThreads(restaurantId, 'active'),
        listThreads(restaurantId, 'archived'),
      ]);
      return { threads: [...active.threads, ...archived.threads].map(toMetadata) };
    },
    async initialize() {
      const thread = await createThread(restaurantId);
      return { remoteId: thread.id };
    },
    async fetch(threadId) {
      return toMetadata(await getThread(restaurantId, threadId));
    },
    async rename(remoteId, title) {
      await updateThread(restaurantId, remoteId, { title });
    },
    async archive(remoteId) {
      await updateThread(restaurantId, remoteId, { status: 'archived' });
    },
    async unarchive(remoteId) {
      await updateThread(restaurantId, remoteId, { status: 'active' });
    },
    async delete(remoteId) {
      await deleteThread(restaurantId, remoteId);
    },
    async generateTitle(remoteId) {
      const thread = await getThread(restaurantId, remoteId);
      return createAssistantStream((controller) => {
        controller.appendText(thread.title);
      });
    },
  };
}
